/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the quarantine pharmacy stock modal component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions } from '@material-ui/core';

import { FormRenderProps } from 'react-final-form';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import HiddenField from '../../../components/FinalForm/FinalFormControls/HiddenField';
import { PharmacyActions } from '../../../store/modules/drugmanagement/pharmacy';
import useAsyncFunction from '../../../hooks/useAsyncFunction';

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IQuarantinePharmacyStockModalProps {
    pharmacyId: number;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IQuarantinePharmacyStockFormProps extends FormRenderProps {
    pharmacyId: number;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const QuarantinePharmacyStockForm: React.FunctionComponent<IQuarantinePharmacyStockFormProps> = ({
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                enqueueSnackbar(`Site stock has been quarantined`, { variant: 'success' });

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            const errorMessage = error.message ?
                error.message :
                'Unknown error';

            enqueueSnackbar(`An error occurred while attempting to change the drug shipment's status: ${errorMessage}`, { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    return (
        <>
            <DialogContent>
                Warning: This will set all the stock available at the pharmacy to &quot;Quarantined&quot; if you continue.
                <HiddenField
                    fieldName="id"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const QuarantinePharmacyStockModal: React.FunctionComponent<IQuarantinePharmacyStockModalProps> = ({
    pharmacyId,
    modalOpen,
    setModalOpen,
    onCloseClick,
    onFormSave
}) => {
    const initialValues = Object.assign({}, { id: pharmacyId });

    const quarantineStockForm = React.useCallback((props: FormRenderProps) => {
        return (
            <QuarantinePharmacyStockForm
                pharmacyId={pharmacyId}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [setModalOpen, onFormSave, pharmacyId]);

    const asyncSave = useAsyncFunction(PharmacyActions.quarantineStock, PharmacyActions.quarantineStockSuccess, PharmacyActions.quarantineStockFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Quarantine Pharmacy Stock
                </DialogTitle>
                <AsyncForm
                    initialValues={initialValues}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={quarantineStockForm}
                />
            </Dialog>
        </>
    );
}

export default QuarantinePharmacyStockModal;